import type { ActingFor, ActForOffer, ActForPerson, DocumentView } from '@myrewards/contracts';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Dialog,
  Heading,
  Label,
  Modal,
  ModalOverlay,
  RadioButton,
  RadioField,
  RadioGroup,
  Text,
  TextArea,
  TextField,
} from 'react-aria-components';
import { Button, cx, HelpShownTag, Icon, useHelpAnchor } from '../ui';
import { FileDrop, takePastedImages } from './FileDrop';
import { Avatar, FileIcon, Pair, ScanWords, shared } from './parts';
import { useStagedFiles, type StagedItem } from './useStagedFiles';
import {
  actForProblemWords,
  BLOCKED_WORDS,
  firstName,
  ROLE_WORD,
  sizeWords,
  SOURCE_WORDS,
  UNREADABLE_WORDS,
  type FileSource,
} from './words';
import s from './ActingFor.module.css';

/** What the action was done with, for the page that says it's done (the Approved board). */
export interface ActForDone {
  person: ActForPerson;
  note: string | null;
  files: DocumentView[];
}

export interface ActForStepProps {
  /** "Approve for an Executive", "Approve for a Sector Lead", "Send for a Sector Lead". */
  title: string;
  /** The line above the title: "SDB-2026-0156 · Approve with a $2,913 top-up". */
  subtitle?: string;
  /** The sentence under the title; by default it says who it's for and that they're told. */
  intro?: string;
  /** The Stock Admin acting: their initials beside Recorded as. */
  actorName: string;
  /** Who may be named, from the page's own facts (`ApprovalDetail.actingFor`, `GET people`). */
  offer: ActForOffer;
  firstNameOf: (id: string) => string;
  /** Under the list of Executives; by default "Any one Executive can approve this. …". */
  whoHint?: string;
  /** The who field's label; by default "Who is this for?" (the Budget board's is "Executive"). */
  whoLabel?: string;
  /** Beside a person filled in; by default "Filled in from the award’s sector". */
  fixedHint?: string;
  /** "When you approve", "When you send", "Check before saving". */
  summaryTitle?: string;
  /**
   * The lines of what will happen, as `<li>`s, for the person named so far (null before anyone
   * is) and what the notice will carry.
   */
  summary: (personId: string | null, added: { note: boolean; files: number }) => ReactNode;
  /** "Approved by Meredith Okonkwo (Stock Admin) for Elena Brightwater (Executive)". */
  recorded: (person: ActForPerson) => string;
  /**
   * A line after the summary for the person named so far (null before anyone is), in the info
   * notice's colours (SameAsk board: "If Elena had asked for a different split…").
   */
  afterSummary?: (personId: string | null) => ReactNode;
  /** "Approve for Elena", "Send for Graham". Before anyone is named, the button reads the title. */
  confirmLabel: (person: ActForPerson) => string;
  /** The button before anyone is named, when the title carries more ("… · EDB-2026-0019"). */
  unnamedLabel?: string;
  /**
   * By default "If it isn't what Elena wanted, Elena can hold the award from the notice."; null
   * leaves the footer to its buttons (the LeadApprove board).
   */
  ifNotWanted?: ((person: ActForPerson) => string) | null;
  /** The note's hint for the person's first name; by default "… It's kept with the award." */
  noteHint?: (first: string) => string;
  /** Fields of the action itself (a note to whoever entered it), above Files. */
  children?: ReactNode;
  /** Whether those fields are complete: the confirm waits for them. Default true. */
  ready?: boolean;
  /** Opens in the page, for a page that already decides inline (a delegate's entry). */
  inline?: boolean;
  /**
   * After an answer lost on the way back: the line saying the first attempt is being checked.
   * While set, who it's for, the note and the files can't change, so the next confirm checks
   * what was sent; Cancel and the confirm still work.
   */
  frozen?: string | null;
  /** The refusal's words; by default the server's own (`issues`, then `detail`). */
  refusalWords?: (error: unknown) => string;
  /**
   * Saves the action, naming the person, the note and the staged files. Throw to keep the step
   * open: the refusal shows in its live region. The page closes the step when this resolves.
   */
  onConfirm: (actingFor: ActingFor, done: ActForDone) => Promise<void>;
  onCancel: () => void;
}

/** A file's check in the step: on its way up, then the virus check's verdict. */
function ItemScan({ item }: { item: StagedItem }): ReactElement | null {
  if (item.state === 'failed' || item.unreadable) return null;
  if (item.state === 'uploading' || item.document === null)
    return <ScanWords status="pending" label="Uploading" />;
  return <ScanWords status={item.document.scanStatus} />;
}

/** Why a file can't stay: refused before or by the upload, or by the virus check. */
function itemProblem(item: StagedItem): string | null {
  if (item.error !== null) return item.error;
  if (item.unreadable) return UNREADABLE_WORDS;
  const status = item.document?.scanStatus;
  return status === 'malicious' || status === 'unscannable' ? BLOCKED_WORDS[status] : null;
}

/**
 * The step a Stock Admin takes before anything is saved for someone else (APP-148 "The step",
 * D3–D5, D14–D16): who it's for (a radio list of today's Executives, or the award's own Sector
 * Lead filled in), files (chosen, dropped or pasted, each staged at once) and a note, both
 * optional, then what will happen, how it's recorded, and the action named for the person.
 *
 * In a dialog by default; `inline` opens it in the page. Each step stages its own files and
 * starts empty, so a file staged for one action is never sent by another. Cancel removes what it
 * staged; focus goes back to whatever opened it.
 */
export function ActForStep(props: ActForStepProps): ReactElement {
  const {
    title,
    subtitle,
    actorName,
    offer,
    firstNameOf,
    summaryTitle = 'When you confirm',
    summary,
    afterSummary,
    recorded,
    confirmLabel,
    children,
    ready = true,
    inline = false,
    onConfirm,
    onCancel,
  } = props;
  const files = useStagedFiles();
  /** The last files added didn't all fit: the action's 20 were reached (shown while still full). */
  const [tooMany, setTooMany] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Switching what the step does (Approve, Return, Reject for a lead) starts without the last
  // action's refusal.
  const [titleSeen, setTitleSeen] = useState(title);
  if (titleSeen !== title) {
    setTitleSeen(title);
    setError(null);
  }
  const frozen = props.frozen ?? null;
  /** Who it's for, the note and the files can't change: saving, or checking a first attempt. */
  const locked = pending || frozen !== null;
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const mounted = useRef(true);
  // What Help's "Show me" calls the step, where it opens in the page and Help stays beside it.
  // A dialog sits outside the page's main region, where Show me never looks (M7).
  const anchor = useHelpAnchor('act-for-step');

  // In the page, the step takes focus when it opens and gives it back to its opener when it
  // closes. The dialog does both by itself.
  useLayoutEffect(() => {
    if (!inline) return;
    const opener = document.activeElement;
    headingRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [inline]);

  // When the step closes and whatever opened it has gone (a refusal moved the page on), focus
  // goes to the page's main heading, never to <body>. The dialog gives focus back first, when it
  // can; this only catches what it couldn't.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestAnimationFrame(() => {
        // Mounted again (React's dev double mount): the step is still open.
        if (mounted.current) return;
        const active = document.activeElement;
        if (active !== null && active !== document.body) return;
        document.querySelector<HTMLElement>('main h1')?.focus({ preventScroll: true });
      });
    };
  }, []);

  // A person no longer offered (taken off the Executives list while the step was open) is
  // dropped, so the confirm waits for a current one.
  const fixed = offer.fixed ? (offer.people[0] ?? null) : null;
  const person = fixed ?? offer.people.find((p) => p.id === picked) ?? null;
  const first = person === null ? null : firstNameOf(person.id) || firstName(person.name);
  const trimmed = note.trim();
  const label = person === null ? (props.unnamedLabel ?? title) : confirmLabel(person);
  // A file the virus check refused holds the confirm until it's removed (the scan gate).
  const canConfirm = person !== null && ready && !files.busy && !files.blocked && !pending;

  const intro =
    props.intro ??
    (fixed !== null
      ? `You’re doing this for ${first ?? fixed.name}. It’s recorded as your action, done for ${first ?? fixed.name}, and ${first ?? fixed.name} is told.`
      : `You’re doing this for ${offer.role === 'executive' ? 'an Executive' : 'a Sector Lead'}. It’s recorded as your action, done for them, and they’re told.`);
  const whoHint =
    props.whoHint ??
    (offer.role === 'executive'
      ? 'Any one Executive can approve this. Naming one records who it was for.'
      : 'Filled in from the award’s sector.');
  const noteHint = (
    props.noteHint ?? ((name) => `${name} sees it in the notice. It’s kept with the award.`)
  )(first ?? (offer.role === 'executive' ? 'The Executive you name' : 'The Sector Lead'));
  const footnote =
    props.ifNotWanted === null
      ? null
      : (props.ifNotWanted ??
        ((p: ActForPerson) => {
          const name = firstNameOf(p.id) || firstName(p.name);
          return `If it isn’t what ${name} wanted, ${name} can hold the award from the notice.`;
        }));
  const ifNotWanted =
    footnote === null ? null : person === null ? `Choose who it’s for first.` : footnote(person);

  const cancel = (): void => {
    if (pending) return;
    files.discard();
    onCancel();
  };

  const confirm = async (): Promise<void> => {
    if (person === null || !canConfirm) return;
    setPending(true);
    setError(null);
    const fileIds = files.documents.map((d) => d.id);
    const actingFor: ActingFor = {
      personId: person.id,
      ...(trimmed !== '' && { note: trimmed }),
      ...(fileIds.length > 0 && { fileIds }),
    };
    try {
      // The files are the action's once it's saved; until then, and if it's refused, the step's.
      await files.confirm(() =>
        onConfirm(actingFor, {
          person,
          note: trimmed === '' ? null : trimmed,
          files: [...files.documents],
        }),
      );
    } catch (e) {
      if (mounted.current) setError((props.refusalWords ?? actForProblemWords)(e));
    } finally {
      if (mounted.current) setPending(false);
    }
  };

  // Every way in (chosen, dropped, pasted in the zone or anywhere in the step) takes files here,
  // so the action's 20 hold for each and the refusal says so (R57).
  const take = (list: readonly File[], source: FileSource): void => {
    const fits = list.slice(0, files.room);
    setTooMany(fits.length < list.length);
    if (fits.length > 0) files.add(fits, source);
  };

  const onPaste = (event: ClipboardEvent<HTMLDivElement>): void => {
    if (locked) return;
    const images = takePastedImages(event);
    if (images.length > 0) take(images, 'pasted');
  };

  const heading = inline ? (
    <h2 id={headingId} ref={headingRef} tabIndex={-1} className={cx('disp', s.title)}>
      {title}
    </h2>
  ) : (
    <Heading slot="title" id={headingId} className={cx('disp', s.title)}>
      {title}
    </Heading>
  );

  const body = (
    // A screenshot pasted anywhere in the step is a file (D16); text pastes as text.
    <div className={s.step} onPaste={onPaste}>
      <div className={s.head}>
        {subtitle !== undefined && <p className={cx('eyebrow', s.eyebrow)}>{subtitle}</p>}
        {heading}
        <HelpShownTag shown={anchor.shown} />
        <p className={s.intro}>{intro}</p>
      </div>

      {fixed !== null ? (
        <div className={s.section}>
          <p className={s.fieldLabel}>{props.whoLabel ?? 'Who is this for?'}</p>
          <div className={s.fixedPerson}>
            <Avatar name={fixed.name} />
            <span className={s.personText}>
              <span className={s.personName}>{fixed.name}</span>
              <span className={s.personTitle}>{fixed.title ?? ROLE_WORD[offer.role]}</span>
            </span>
            <span className={s.lock}>
              <Icon name="lock" size={14} />
              {props.fixedHint ?? 'Filled in from the award’s sector'}
            </span>
          </div>
        </div>
      ) : (
        <RadioGroup
          className={s.section}
          value={person?.id ?? null}
          onChange={setPicked}
          isRequired
          isDisabled={locked}
        >
          <Label className={s.fieldLabel}>
            {props.whoLabel ?? 'Who is this for?'} <span className={s.required}>Required</span>
          </Label>
          {offer.people.length === 0 ? (
            <p className={s.noPeople}>
              {offer.role === 'executive'
                ? 'No one is on the Executives list today. A System Admin adds Executives in Roles & lists.'
                : 'This sector has no Sector Lead today. A System Admin names one in Roles & lists.'}
            </p>
          ) : (
            <div className={s.people}>
              {offer.people.map((p) => (
                <RadioField key={p.id} value={p.id}>
                  <RadioButton className={s.personTile}>
                    <Avatar name={p.name} />
                    <span className={s.personText}>
                      <span className={s.personName}>{p.name}</span>
                      {p.title !== null && <span className={s.personTitle}>{p.title}</span>}
                    </span>
                    <span className={s.radioMark} aria-hidden="true" />
                  </RadioButton>
                </RadioField>
              ))}
            </div>
          )}
          <Text slot="description" className={s.hint}>
            {whoHint}
          </Text>
        </RadioGroup>
      )}

      {children}

      <div className={s.section}>
        <p className={s.fieldLabel}>
          Files <span className={s.optional}>Optional</span>
        </p>
        <FileDrop onFiles={take} refused={tooMany && files.room === 0} isDisabled={locked} />
        {files.items.length > 0 && (
          <ul className={shared.files} aria-label="Files you’re adding">
            {files.items.map((item) => {
              const problem = itemProblem(item);
              return (
                <li key={item.key} className={shared.file} aria-label={item.name}>
                  <FileIcon contentType={item.document?.contentType ?? item.contentType} />
                  <span className={shared.fileText}>
                    <span className={shared.fileName}>{item.name}</span>
                    <span className={shared.fileMeta}>
                      <span>
                        {sizeWords(item.sizeBytes)} · {SOURCE_WORDS[item.source]}
                      </span>
                      <ItemScan item={item} />
                    </span>
                    {problem !== null && (
                      <span className={s.fileError}>
                        <Icon name="alert" size={14} />
                        {problem}
                      </span>
                    )}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${item.name}`}
                    isPending={item.removing}
                    isDisabled={locked}
                    onPress={() => {
                      void files.remove(item.key);
                    }}
                  >
                    Remove
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <TextField className={s.section} value={note} onChange={setNote} isDisabled={locked}>
        <Label className={s.fieldLabel}>
          Note <span className={s.optional}>Optional</span>
        </Label>
        <TextArea className={s.textarea} maxLength={2000} />
        <Text slot="description" className={s.hint}>
          {noteHint}
        </Text>
      </TextField>

      <div className={s.summary}>
        <p className={cx('eyebrow', s.summaryTitle)}>{summaryTitle}</p>
        <ul className={s.summaryList}>
          {summary(person?.id ?? null, { note: trimmed !== '', files: files.documents.length })}
        </ul>
        {person !== null && (
          <div className={s.recorded}>
            <Pair actor={actorName} person={person.name} />
            <p className={s.recordedText}>
              <strong>Recorded as:</strong> {recorded(person)}
            </p>
          </div>
        )}
      </div>

      {afterSummary !== undefined && (
        <p className={s.tip}>
          <Icon name="info" size={16} />
          <span>{afterSummary(person?.id ?? null)}</span>
        </p>
      )}

      {frozen !== null && (
        <p className={s.tip}>
          <Icon name="info" size={16} />
          <span>{frozen}</span>
        </p>
      )}

      <p className="visually-hidden" role="status" aria-live="polite">
        {pending ? `${label} in progress.` : ''}
      </p>
      {/* Mounted and empty until there's a refusal, so its words are announced as they arrive. */}
      <p className={cx(s.error, error === null && s.errorEmpty)} role="alert">
        {error}
      </p>

      <div className={s.footer}>
        {ifNotWanted !== null && <p className={s.footnote}>{ifNotWanted}</p>}
        <div className={s.buttons}>
          <Button variant="ghost" onPress={cancel} isDisabled={pending}>
            Cancel
          </Button>
          <Button
            variant="navy"
            isDisabled={!canConfirm && !pending}
            isPending={pending}
            onPress={() => {
              void confirm();
            }}
          >
            {label}
          </Button>
        </div>
      </div>
    </div>
  );

  if (inline)
    return (
      <section className={s.inline} aria-labelledby={headingId} {...anchor.props}>
        {body}
      </section>
    );

  return (
    <ModalOverlay
      isOpen
      onOpenChange={(open) => {
        if (!open) cancel();
      }}
      isDismissable={!pending}
      isKeyboardDismissDisabled={pending}
      className={s.overlay}
    >
      <Modal className={s.modal}>
        <Dialog className={s.dialog} aria-labelledby={headingId}>
          {body}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
